import {createContext, FC, ReactNode, useContext, useEffect, useRef, useState} from "react";
import {bool, string} from "yup";
import {useAuthenticationContext} from "../auth/AuthenticationContext";
import {VivoRootModel} from "../Models";
import {VivoPortal} from "../AppConstants";
import EnumMemberGroupAliases = VivoPortal.EnumMemberGroupAliases;
import PiwikPro, {PageViews, UserManagement} from "@piwikpro/react-piwik-pro";

export const PiwikContext = createContext<PiwikContextProps>({
    setters: {
        setPiwikScreen: () => {
        },
        setPiwikUser: () => {
        }
    },
    trackers: {
        trackVirtualPageView: () => {
        },
        trackUserData: () => {
        }
    },
    utils: {
        updateUserData: () => {
        }
    },
    events: {
        triggerLoginEvent: (hasLoggedIn: boolean, userId: number | null | undefined, userLogin: string | null | undefined) => {
        },
        triggerProfileUpdateEvent: (userId: number, userLogin: string) => {
        },
        triggerOrderPlacedEvent: (orderNUmber: string) => {
        },
        triggerOrderCompletedEvent: (orderNUmber: string, finalStatus: string) => {
        },
        triggerFileUploadedEvent: (fileName: string, extension: string | null) => {
        },
        triggerFileDownloadedEvent: (fileName: string, extension: string | null) => {
        },
    }
});

export function usePiwikContext() {
    const context = useContext(PiwikContext);
    if (!context) {
        throw new Error('usePiwikContext must be used within an PiwikContextProvider');
    }
    return context;
}

export const PiwikContextProvider: FC<{ children: ReactNode }> = ({children}) => {
    const piwikScreenRef = useRef<PiwikScreen>(
        {
            name: '===',
            language: '===',
            title: '===',
            url: '===',
            category: 'support',
            site_name: 'VIVO ColourPortal'
        });
    const piwikUserRef = useRef<PiwikUser>(
        {
            id: 0,
            email: "",
            name: "anonymous",
            login: "",
            sap: "",
            user_type: ""
        });
    const {rootModel} = useAuthenticationContext();
    useEffect(() => {
        setPiwikScreen("category", piwikScreenRef.current.category);
    }, []);

    function setPiwikScreen<T extends keyof PiwikScreen>(key: T, value: PiwikScreen[T]) {
        // if (piwikScreenRef.current[key] == value)
        //     return;
        // //@ts-ignore
        // window.dataLayer = window.dataLayer || [];
        // //@ts-ignore
        // window.dataLayer.push({
        //     [`screen.${key}`]: value,
        // });
        piwikScreenRef.current[key] = value;
    }

    function setPiwikUser<T extends keyof PiwikUser>(key: T, value: PiwikUser[T]) {
        // if (piwikUserRef.current[key] == value)
        //     return;
        // //@ts-ignore
        // window.dataLayer = window.dataLayer || [];
        // //@ts-ignore
        // window.dataLayer.push({
        //     [`user.${key}`]: value,
        // });
        piwikUserRef.current[key] = value;
    }

    // useEffect(() => {
    //     updateUserData();
    //     trackUserData();
    // }, [rootModel]);
    function isCurrentUserInRole(roleMask: EnumMemberGroupAliases, role: EnumMemberGroupAliases) {
        return roleMask && (roleMask & role) > 0;
    }

    function updateUserData(vivoRootModel?: VivoRootModel | null) {
        const userData = vivoRootModel === undefined ? rootModel : vivoRootModel;
        // this is just for session reset - not for reporting
        UserManagement.setUserId(userData?.CurrentUserProfile.Id.toString() ?? '===');
        setPiwikUser("id", userData?.CurrentUserProfile.Id ?? 0);
        setPiwikUser("name", userData?.CurrentUserProfile.FullName ?? "anonymous");
        setPiwikUser("login", userData?.CurrentUserProfile.UserName ?? '===');
        setPiwikUser("email", userData?.CurrentUserProfile.Email ?? '===');
        setPiwikUser("sap", userData?.CurrentUserProfile.SapNumber ?? '===');
        //setPiwikUser("email", userData?.CurrentUserProfile.Email ?? "");
        let role = isCurrentUserInRole((userData?.CurrentUserProfile.MemberRoles ?? 0), EnumMemberGroupAliases.SuperAdmin) ? "super-admin" : "";
        if (!role && isCurrentUserInRole((userData?.CurrentUserProfile.MemberRoles ?? 0), EnumMemberGroupAliases.Admin))
            role = "admin";
        if (!role && isCurrentUserInRole((userData?.CurrentUserProfile.MemberRoles ?? 0), EnumMemberGroupAliases.Supervisor))
            role = "supervisor";
        if (!role && isCurrentUserInRole((userData?.CurrentUserProfile.MemberRoles ?? 0), EnumMemberGroupAliases.Employee))
            role = "employee";
        if (!role && isCurrentUserInRole((userData?.CurrentUserProfile.MemberRoles ?? 0), EnumMemberGroupAliases.Customer))
            role = "customer";
        if (!role && isCurrentUserInRole((userData?.CurrentUserProfile.MemberRoles ?? 0), EnumMemberGroupAliases.User))
            role = "user";
        if (!role)
            role = "unknown";
        setPiwikUser("user_type", role)
    }

    function triggerLoginEvent(hasLoggedIn: boolean, userId: number | null | undefined, userLogin: string | null | undefined) {
        const eventName = hasLoggedIn ? "login" : "logout";
        const eventData: PiwikEventLogin = {
            eventCat: "account",
            eventAct: "login",
            eventLab: eventName,
            "user.id": userId ?? 0,
            "user.login": userLogin ?? "==="
        };
        pushDataLayerEvent(eventData, eventName);
    }

    function triggerProfileUpdateEvent(userId: number, userLogin: string) {
        const eventData: PiwikEventLogin = {
            eventCat: "account",
            eventAct: "settings",
            eventLab: "user_profile_updates",
            "user.id": userId,
            "user.login": userLogin
        };
        pushDataLayerEvent(eventData, "user_profile_updates");
    }

    function triggerOrderPlacedEvent(orderNUmber: string) {
        const eventData: PiwikEventLogin = {
            eventCat: "order",
            eventAct: "submit",
            eventLab: orderNUmber,
            "user.id": piwikUserRef.current.id,
            "user.login": piwikUserRef.current.login
        };
        pushDataLayerEvent(eventData, "order_created");
    }

    function triggerOrderCompletedEvent(orderNUmber: string, finalStatus: string) {
        const eventData: PiwikEventLogin = {
            eventCat: "order",
            eventAct: "complete",
            eventLab: `${orderNUmber} / ${finalStatus}`,
            "user.id": piwikUserRef.current.id,
            "user.login": piwikUserRef.current.login
        };
        pushDataLayerEvent(eventData, "order_completed");
    }

    function triggerFileUploadedEvent(fileName: string, extension: string | null) {
        const eventData: PiwikEventFile = {
            eventCat: "document",
            eventAct: "share",
            eventLab: fileName + (extension ? `.${extension}` : ""),
            document: {
                type: extension ?? "{no-extension}",
                title: fileName
            },
            "user.id": piwikUserRef.current.id,
            "user.login": piwikUserRef.current.login
        };
        pushDataLayerEvent(eventData, "document_share");
    }

    function triggerFileDownloadedEvent(fileName: string, extension: string | null) {
        const eventData: PiwikEventFile = {
            eventCat: "document",
            eventAct: "download",
            eventLab: fileName + (extension ? `.${extension}` : ""),
            document: {
                type: extension ?? "{no-extension}",
                title: fileName
            },
            "user.id": piwikUserRef.current.id,
            "user.login": piwikUserRef.current.login
        };
        pushDataLayerEvent(eventData, "document_download");
    }

    function pushDataLayerEvent(eventData: any, eventName: string, prefix?: string) {

        const prefixedData = !prefix ? eventData : Object.keys(eventData).reduce((acc, key) => {
            // Skip the 'event' key if it exists
            if (key === 'event') return acc;

            // Add prefix to all other keys
            acc[`${prefix}.${key}`] = eventData[key];
            return acc;
        }, {} as Record<string, any>);

        // Construct the final event object
        const finalEvent = {
            event: eventName,
            ...prefixedData
        };

        // Push to dataLayer
        //@ts-ignore
        window.dataLayer = window.dataLayer || [];
        //@ts-ignore
        window.dataLayer.push(finalEvent);
    }

    function trackUserData() {
        pushDataLayerEvent(piwikUserRef.current, 'user_data_ready', 'user')
    }

    function trackVirtualPageView() {
        // CustomDimensions.setCustomDimensionValue(4, piwikScreenRef.current.name); // screen.name
        // CustomDimensions.setCustomDimensionValue(5, piwikScreenRef.current.language); // screen.language
        // CustomDimensions.setCustomDimensionValue(6, piwikScreenRef.current.title); // screen.title
        // CustomDimensions.setCustomDimensionValue(7, piwikScreenRef.current.url); // screen.url
        // CustomDimensions.setCustomDimensionValue(8, piwikScreenRef.current.category); // screen.category
        // CustomDimensions.setCustomDimensionValue(9, piwikScreenRef.current.site_name); // screen.site_name
        // PageViews.trackPageView(piwikScreenRef.current.name);
        pushDataLayerEvent(piwikScreenRef.current, 'screen_data_ready', 'screen');
    }


    return (
        <PiwikContext.Provider value={{
            //piwikScreen:piwikScreenRef.current, 
            setters: {
                setPiwikScreen,
                setPiwikUser
            },
            trackers: {
                trackVirtualPageView,
                trackUserData
            },
            utils: {
                updateUserData
            },
            events: {
                triggerLoginEvent,
                triggerProfileUpdateEvent,
                triggerOrderPlacedEvent,
                triggerOrderCompletedEvent,
                triggerFileUploadedEvent,
                triggerFileDownloadedEvent,
            }
        }}>
            {children}
        </PiwikContext.Provider>
    );
};

export interface PiwikContextProps {
    setters: {
        setPiwikScreen: <T extends keyof PiwikScreen>(key: T, value: PiwikScreen[T]) => void,
        setPiwikUser: <T extends keyof PiwikUser>(key: T, value: PiwikUser[T]) => void,
    }
    trackers: {
        trackVirtualPageView: () => void
        trackUserData: () => void,
    },
    events: {
        triggerLoginEvent: (hasLoggedIn: boolean, userId: number | null | undefined, userLogin: string | null | undefined) => void,
        triggerProfileUpdateEvent: (userId: number, userLogin: string) => void,
        triggerOrderPlacedEvent: (orderNUmber: string) => void,
        triggerOrderCompletedEvent: (orderNUmber: string, finalStatus: string) => void,
        triggerFileUploadedEvent: (fileName: string, extension: string | null) => void,
        triggerFileDownloadedEvent: (fileName: string, extension: string | null) => void,
    },
    utils: {
        updateUserData: (vivoRootModel?: VivoRootModel | null) => void,
    }
}

export interface PiwikScreen {
    name: string;
    language: string;
    title: string;
    url: string;
    category: string;
    site_name: string;
}

export interface PiwikUser {
    id: number;
    email: string;
    name: string;
    login: string;
    sap: string;
    user_type: string;
}

export interface PiwikEventLogin {
    eventCat: string;
    eventAct: string;
    eventLab: string;
    ["user.id"]: number | null | undefined;
    ["user.login"]: string | null | undefined;
}

export interface PiwikEventFile {
    eventCat: string;
    eventAct: string;
    eventLab: string;
    document: {
        type: string;
        title: string;
    },
    ["user.id"]: number | null | undefined;
    ["user.login"]: string | null | undefined;
}

export interface PiwikDocument {
    name: string;
    title: string;
}